/**
 * When the SPA runs on a TrustVid tenant hostname ({slug}.localhost, {slug}.stage.trustvid.ai, {slug}.trustvid.ai),
 * switching to another account may require changing the origin to that account's host_slug (or the global app host).
 *
 * Platform hosts (app.stage, api.stage, app.trustvid.ai, …) are **global** — SuperAdmin account switch stays in-app
 * so localStorage (JWT) is not lost across subdomains.
 */

/** Labels that are platform hosts, not tenant slugs — keep in sync with API `RESERVED_SLUGS` in tenant-host.js */
const RESERVED_PLATFORM_HOST_LABELS = new Set([
  "www",
  "api",
  "app",
  "admin",
  "cdn",
  "static",
  "mail",
  "ftp",
  "stage",
  "stg",
  "stgapi",
  "stgapp",
]);

const isReservedPlatformLabel = (label) => RESERVED_PLATFORM_HOST_LABELS.has(String(label || "").toLowerCase());

export function parseTenantBrowsingContext(loc) {
  if (!loc || typeof loc.hostname !== "string") {
    return { kind: "global" };
  }
  const protocol = loc.protocol || "http:";
  const host = loc.hostname.toLowerCase();
  const port = loc.port || "";

  const local = /^([a-z0-9-]{2,63})\.localhost$/i.exec(host);
  if (local) {
    const slug = local[1].toLowerCase();
    if (isReservedPlatformLabel(slug)) {
      return { kind: "global" };
    }
    return { kind: "local_tenant", slug, protocol, port };
  }

  const stage = /^([a-z0-9-]{2,63})\.stage\.trustvid\.ai$/i.exec(host);
  if (stage) {
    const slug = stage[1].toLowerCase();
    if (isReservedPlatformLabel(slug)) {
      return { kind: "global" };
    }
    return { kind: "stage_tenant", slug, protocol, port: "" };
  }

  const prod = /^([a-z0-9-]{2,63})\.trustvid\.ai$/i.exec(host);
  if (prod) {
    const s = prod[1].toLowerCase();
    if (isReservedPlatformLabel(s)) {
      return { kind: "global" };
    }
    return { kind: "prod_tenant", slug: s, protocol, port: "" };
  }

  return { kind: "global" };
}

export function computeTenantAppOrigin(ctx, targetHostSlug) {
  const normalized =
    typeof targetHostSlug === "string" && targetHostSlug.trim() ? targetHostSlug.trim().toLowerCase() : null;
  const portSuffix = ctx.port ? `:${ctx.port}` : "";

  if (ctx.kind === "local_tenant") {
    if (!normalized) {
      return `${ctx.protocol}//localhost${portSuffix}`;
    }
    return `${ctx.protocol}//${normalized}.localhost${portSuffix}`;
  }
  if (ctx.kind === "stage_tenant") {
    if (!normalized) {
      return `${ctx.protocol}//app.stage.trustvid.ai`;
    }
    return `${ctx.protocol}//${normalized}.stage.trustvid.ai`;
  }
  if (ctx.kind === "prod_tenant") {
    if (!normalized) {
      return `${ctx.protocol}//app.trustvid.ai`;
    }
    return `${ctx.protocol}//${normalized}.trustvid.ai`;
  }
  return null;
}

/**
 * @returns {string|null} full origin (protocol//host[:port]) when a full navigation is required; otherwise null to keep SPA routing.
 */
export function buildPostSwitchAppOrigin(loc, targetHostSlug) {
  const ctx = parseTenantBrowsingContext(loc);
  if (ctx.kind === "global") {
    return null;
  }
  const next = computeTenantAppOrigin(ctx, targetHostSlug);
  const current = `${loc.protocol}//${loc.host}`;
  if (!next || next === current) {
    return null;
  }
  return next;
}

export { RESERVED_PLATFORM_HOST_LABELS };
